import { CheckCircle2 } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Lovable-style step card for the onboarding flow. Mirrors the visual
 * treatment of `fresh-dashboard/src/components/lifecycle/PreTrialScreen.tsx`
 * — a rounded card with an icon-on-the-left header, title and subtitle.
 *
 * Unlike the Lovable wizard (one step at a time) the Unify onboarding keeps
 * every step rendered so users can revisit business details / GBP after
 * completion. Cards that are neither active nor completed are softened so
 * the eye is pulled to the active step.
 */
export function OnboardingStepCard({ icon: Icon, title, subtitle, isCompleted, isActive, children, className, ...rest }) {
	const status = isCompleted ? 'completed' : isActive ? 'active' : 'pending';

	return (
		<section
			className={cn(
				'rounded-2xl border border-border/60 bg-card p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:p-8',
				!isActive && !isCompleted ? 'opacity-70' : null,
				className
			)}
			data-step-status={status}
			{...rest}
		>
			<div className="flex items-start gap-4">
				<div
					className={cn(
						'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
						isCompleted ? 'bg-success-soft text-success-soft-foreground' : 'bg-primary/10 text-primary'
					)}
				>
					{isCompleted ? <CheckCircle2 className="h-5 w-5" /> : Icon ? <Icon className="h-5 w-5" /> : null}
				</div>
				<div className="min-w-0 flex-1">
					<h2 className="text-lg font-extrabold tracking-tight text-foreground md:text-xl">{title}</h2>
					{subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
				</div>
			</div>
			{children ? <div className="mt-6">{children}</div> : null}
		</section>
	);
}
